import { z } from "zod";

/**
 * Why a turn's stream ended without an answer. Captured at the FETCH boundary
 * because the AI SDK only hands `onError` a bare `Error` whose message is the
 * server's response text — and deciding anything from that text is the
 * string-sniffing this codebase has already been burnt by (see the webapp's
 * `GPTChatError`). Rendering it is the other mistake: the text is whatever the
 * server or a provider wrote, in English, for a developer.
 *
 * The two arms mean opposite things for recovery:
 * - `rejected` — the server ANSWERED with a status (out of credits 429, a turn
 *   already in progress 409, a bad request, a fault before the stream). No turn
 *   is running, so there is nothing to wait for.
 * - `failed` — the server ACCEPTED the turn and then reported, inside the
 *   stream, that it failed: an `error` part (a provider error, a finish that
 *   threw before the turn was written). On every surface the turn is never
 *   added to the conversation on that path (the spend of a credit-metered turn
 *   is still settled), so there is nothing to wait for either.
 * - `disconnected` — either the request never got an answer, or an accepted
 *   stream died in flight with no `error` part. The server drains its own
 *   stream (`consumeStream()` in api's `streamGeneralChatTurn`), so the turn is
 *   very likely still running to completion and will be persisted.
 */
export type GptStreamFailure =
  | { kind: "rejected"; status: number }
  | { kind: "failed" }
  | { kind: "disconnected" };

/**
 * One chat's failure classification, shared by every `useChat` surface (the
 * webapp's and the dashboard's). Hand `fetch` to the transport and call `take`
 * from `useChat`'s `onError`: it reads what this chat's last request recorded,
 * and clears it, so the next turn starts from a clean slate.
 *
 * `onAccepted` runs when the server takes a turn (2xx), before any answer:
 * the server has persisted it as in progress, which is when lists that show
 * such turns can refetch. Null when the surface has nothing to refresh then.
 */
export function streamFailureRecorder(onAccepted: (() => void) | null): {
  fetch: typeof globalThis.fetch;
  take: () => GptStreamFailure;
} {
  let rejectedStatus: number | null = null;
  let streamFailed = false;
  return {
    // The transport's own `fetch`, wrapping the global one for the single
    // purpose of recording a REJECTION: a non-2xx response is the server
    // declining the turn (it answered), and the SDK is about to collapse that
    // into a bare `Error` whose only distinguishing feature is its message
    // text. `take` reads it a tick later — the SDK gives us no other seam
    // between the response and the error it throws from it.
    fetch: async (input, init) => {
      // Per request: a Regenerate or a Retry reuses the transport, and what the
      // previous request recorded is not this one's outcome.
      rejectedStatus = null;
      streamFailed = false;
      const response = await globalThis.fetch(input, init);
      if (!response.ok) {
        rejectedStatus = response.status;
        return response;
      }
      if (onAccepted !== null) onAccepted();
      if (response.body === null) return response;
      return new Response(
        watchForErrorPart(response.body, () => {
          streamFailed = true;
        }),
        {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        },
      );
    },
    take: () => {
      const status = rejectedStatus;
      const failed = streamFailed;
      rejectedStatus = null;
      streamFailed = false;
      return status !== null
        ? { kind: "rejected", status }
        : failed
          ? { kind: "failed" }
          : { kind: "disconnected" };
    },
  };
}

// The UI-message stream's `error` part. Parsed, not matched as text: a JSON
// payload that merely CONTAINS "error" (an answer about an error rate) is not
// one.
const errorPartSchema = z.object({ type: z.literal("error") });

// One SSE `data:` payload. The question is only "is this the error part":
// the stream's closing `[DONE]`, or any payload that is not JSON, is not one,
// and the SDK's own parser owns everything else about the protocol.
function isErrorPart(payload: string): boolean {
  try {
    return errorPartSchema.safeParse(JSON.parse(payload)).success;
  } catch {
    return false;
  }
}

// Passes the stream through untouched and calls `onErrorPart` when the server
// writes an `error` part into it — the one signal that tells a turn the server
// gave up on from a connection that dropped.
function watchForErrorPart(
  body: ReadableStream<Uint8Array>,
  onErrorPart: () => void,
): ReadableStream<Uint8Array> {
  const decoder = new TextDecoder();
  let pending = "";
  return body.pipeThrough(
    new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        controller.enqueue(chunk);
        pending += decoder.decode(chunk, { stream: true });
        const lines = pending.split("\n");
        const last = lines.pop();
        if (last === undefined)
          throw new Error("should not happen: split returned no line");
        pending = last;
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          if (isErrorPart(line.slice("data: ".length).trimEnd())) onErrorPart();
        }
      },
    }),
  );
}
